import { useState, useEffect, useMemo } from "react";
import Image from "next/image";
import MessageIcon from "../../public/assets/goat-spins-message-instant-withdrawals.svg";
import {
  getActiveMessages,
  getMessageBody,
  updateMessageStatus,
} from "../services/message";

export default function MessagePopup({ user }) {
  const [showMessagesPopup, setShowMessagesPopup] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);

  // ----------- EFFECTS ------------

  useEffect(() => {
    if (!user) return;

    const fetchAllMessages = async () => {
      try {
        const listOfMessages = await getActiveMessages(user.username);

        const messagesWithBody = await Promise.all(
          listOfMessages.map(async (msg) => {
            const detailData = await getMessageBody(msg.messageid);
            return { ...msg, body: detailData.body };
          })
        );

        setMessages(messagesWithBody);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    fetchAllMessages();
  }, [user]);

  useEffect(() => {
    ["BtnToggleCForm", "MainMenu"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.style.display = showMessagesPopup ? "none" : "block";
    });
  }, [showMessagesPopup]);

  // ----------- HANDLERS ------------
  const handleCheckboxChange = (messageid) => {
    setSelectedMessages((prev) =>
      prev.includes(messageid)
        ? prev.filter((id) => id !== messageid)
        : [...prev, messageid]
    );
  };

  const handleOpenMessage = async (message) => {
    setSelectedMessageId(message.messageid);

    if (message.status_description?.toLowerCase() === "unread") {
      try {
        await updateMessageStatus(message.messageid, "read", user.username);
        setMessages((curr) =>
          curr.map((m) =>
            m.messageid === message.messageid
              ? { ...m, status_description: "Read", status_code: "R" }
              : m
          )
        );
      } catch (err) {
        console.debug(
          `Failed to update status for messageId=${message.messageid}`,
          err
        );
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedMessages.length === 0) return;
    try {
      await Promise.all(
        selectedMessages.map((messageId) =>
          updateMessageStatus(messageId, "deleted", user.username)
        )
      );

      setMessages((current) =>
        current.filter((m) => !selectedMessages.includes(m.messageid))
      );

      if (selectedMessageId && selectedMessages.includes(selectedMessageId)) {
        setSelectedMessageId(null);
      }
      setSelectedMessages([]);
    } catch (err) {
      console.error("Error deleting messages:", err);
    }
  };

  // ----------- READ/UNREAD COUNT ------------
  const unreadCount = useMemo(() => {
    return messages.filter(
      (m) => m.status_description?.toLowerCase() === "unread"
    ).length;
  }, [messages]);

  const selectedMessage = useMemo(
    () => messages.find((m) => m.messageid === selectedMessageId),
    [messages, selectedMessageId]
  );

  return (
    <>
      <div
        id="BoxMessages"
        style={{ position: "relative" }}
        onClick={() => setShowMessagesPopup((prev) => !prev)}
      >
        <Image
          width={50}
          height={50}
          src={MessageIcon}
          alt="message-icon"
          className="msgg"
        />
        {unreadCount > 0 && <div className="UnreadBadge">{unreadCount}</div>}
      </div>

      {showMessagesPopup && (
        <div className="MessagePopupBackdrop">
          <div className="MessagePopupContainer">
            <button
              className="CloseBtn"
              onClick={() => setShowMessagesPopup(false)}
            >
              X
            </button>

            {selectedMessageId === null ? (
              <>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <h2 className="msg-h2">Inbox</h2>
                  <div className="pppr" onClick={handleDeleteSelected}>
                    &#128465;
                  </div>
                </div>

                {messages.length === 0 ? (
                  <p>No messages found.</p>
                ) : (
                  messages.map((item) => {
                    const isUnread =
                      item.status_description?.toLowerCase() === "unread";
                    return (
                      <div
                        key={item.messageid}
                        className="MessageTitleBox"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                        onClick={() => handleOpenMessage(item)}
                      >
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedMessages.includes(item.messageid)}
                            onChange={() =>
                              handleCheckboxChange(item.messageid)
                            }
                          />
                          <h3
                            className={isUnread ? "unreadMessage" : ""}
                            style={{
                              cursor: "pointer",
                              marginLeft: 8,
                              marginBottom: 0,
                              fontWeight: isUnread ? "bold" : "normal",
                            }}
                          >
                            {item.title}
                          </h3>
                        </div>
                        {isUnread && <div className="GreenDot" />}
                      </div>
                    );
                  })
                )}
              </>
            ) : (
              <>
                <div className="MessageContentContainer">
                  <div className="MessageTop">
                    <button
                      className="BackBtn"
                      onClick={() => setSelectedMessageId(null)}
                    >
                      ◀
                    </button>
                    <h3>{selectedMessage?.title}</h3>
                  </div>
                  <div
                    className="MessageContent"
                    dangerouslySetInnerHTML={{ __html: selectedMessage?.body }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
